import TWEEN from '@tweenjs/tween.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import planetsData from './config/PlanetsData';

class Controls {
  constructor(spacecast, settings) {
    this.settings = settings;
    this.spacecast = spacecast;
    this.camera = spacecast.camera;
    this.animatePlanets = true;
    this.orbitControls = this.createOrbitControls(this.camera, spacecast.renderer);
    this.travelAnimation = null;
    this.distanceFromFocus = 0;

    this.spacecast.getPlanets().find(planet => (planet.name === 'Earth')).add(this.camera.three.camera);
  }

  createOrbitControls(camera, renderer) {
    // Using a fake camera is a workaround to make OrbitControls to navigate around a moving object.
    // The actual camera position will sync to the fake camera position through the update() function.
    // https://stackoverflow.com/questions/53292145/forcing-orbitcontrols-to-navigate-around-a-moving-object-almost-working
    const fakeCamera = camera.three.camera.clone();
    const control = new OrbitControls(fakeCamera, renderer.domElement);
    control.maxDistance = this.settings.controls.maxDistance;
    control.enableDamping = true;
    control.enablePan = false;
    control.fakeCamera = fakeCamera;
    control.target = this.spacecast.getPlanets()?.find(planet => (planet.name === 'Earth'));
    return control;
  }

  /**
   * This function is called at every frame update.
   */
  update() {
    this.camera.three.camera.copy(this.orbitControls.fakeCamera);
  }

  changeCameraParentTo(cameraNewParent) {
    const cameraParent = this.camera.three.camera.parent
    cameraParent.remove(this.camera.three.camera)
    cameraNewParent.add(this.camera.three.camera)
  }

  getControlParameters(newTargetObjectGroup) {
    let minCameraDistance = 1
    let nearPlane = 1
    if (newTargetObjectGroup.type === 'planet') {
      const planetSettings = planetsData[newTargetObjectGroup.name]
      minCameraDistance = planetSettings.minCameraDistance
      nearPlane = planetSettings.radius
    }

    else if (newTargetObjectGroup.type === 'sun') {
      minCameraDistance = 1000
      nearPlane = 100
    }

    else if (newTargetObjectGroup.type === 'star') {
      minCameraDistance = 3000
      nearPlane = 100
    }

    else if (newTargetObjectGroup.type === 'beam') {
      minCameraDistance = 3
      nearPlane = 0.5
    }

    return { minCameraDistance, nearPlane }
  }

  stopTravel() {
    // Stop travel animation.
    if (this.travelAnimation) this.travelAnimation.stop();
  }

  travelTo(newTargetGroup, callback = () => {}) {
    // Update camera and control parameters.
    const newTargetPosition = newTargetGroup.mainObject.position.clone();
    let {minCameraDistance, nearPlane} = this.getControlParameters(newTargetGroup);
    this.orbitControls.minDistance = minCameraDistance;
    this.camera.setNearPlane(nearPlane);
    this.camera.updateProjectionMatrix();

    // Get camera position in relation to its current parent and translate it to position
    // in relation to the new camera's parent.
    const currentCameraParent = this.camera.three.camera.parent
    const cameraWorldPosition = currentCameraParent.localToWorld(this.camera.three.camera.position.clone())
    const cameraNewLocalPosition = newTargetGroup.worldToLocal(cameraWorldPosition.clone())
    this.orbitControls.fakeCamera.position.copy(cameraNewLocalPosition)
    this.changeCameraParentTo(newTargetGroup)

    const originWorldPosition = currentCameraParent.localToWorld(currentCameraParent.mainObject.position.clone())
    const originLocalPosition = newTargetGroup.worldToLocal(originWorldPosition.clone())

    this.travelAnimation = new TWEEN.Tween(originLocalPosition).to(newTargetPosition, 3000)
    .easing(TWEEN.Easing.Exponential.InOut)
    .onUpdate(nextPosition => {
      this.orbitControls.target = nextPosition
      this.orbitControls.update()
    })
    .onComplete(() => {
      this.recursiveTravel(newTargetGroup.mainObject.position.clone(), callback);
    })
    .onStop(() => {
      callback();
    })
    .start();
  }

  /**
   * This function moves the camera halfway to the finalDestination in a linear fashion within 1 second.
   * At the end, if the remaining distance is bigger than twice of the minCameraDistance, it will
   * call itself recursively to get closer to the finalDestination.
   * 
   * @param {Vector3} finalDestination
   */
  recursiveTravel(finalDestination, callback = () => {}) {
    // See createOrbitControls() to know why we're using fakeCamera.
    const fakeCamera = this.orbitControls.fakeCamera;
    const startPos = fakeCamera.position.clone();
    const travelVector = finalDestination.clone().sub(startPos);
    const halfTravelVector = travelVector.multiplyScalar(0.5);
    const recursiveDestination = startPos.add(halfTravelVector);

    this.travelAnimation = new TWEEN.Tween(fakeCamera.position.clone()).to(recursiveDestination, 1000)
    .onUpdate(positionVector => {
      fakeCamera.position.set(positionVector.x, positionVector.y, positionVector.z);
    })
    .onComplete(() => {
      const minCameraDistance = this.orbitControls.minDistance;
      if (fakeCamera.position.distanceTo(finalDestination) > minCameraDistance * 2) {
        this.recursiveTravel(finalDestination, callback);
      }
      else {
        callback();
        this.stopTravel();
      }
    })
    .onStop(() => {
      callback();
    })
    .start();
  }
}

export default Controls